import { useState, useMemo } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const DOW = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

function iso(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// Pre-order requires at least 3 days lead time
export const AvailabilityCalendar = ({ blocked = [], selected, onSelect, leadDays = 3 }) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const minDate = new Date(today);
  minDate.setDate(minDate.getDate() + leadDays);

  const [view, setView] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const blockedSet = useMemo(() => new Set(blocked), [blocked]);

  const year = view.getFullYear();
  const month = view.getMonth();
  const firstDow = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));

  const monthLabel = view.toLocaleString("default", { month: "long", year: "numeric" });
  const canPrev = new Date(year, month, 1) > new Date(today.getFullYear(), today.getMonth(), 1);

  return (
    <div data-testid="availability-calendar" className="rounded-2xl border border-sugar-border bg-white p-5 sugar-card-shadow">
      <div className="mb-4 flex items-center justify-between">
        <button
          data-testid="cal-prev"
          disabled={!canPrev}
          onClick={() => setView(new Date(year, month - 1, 1))}
          className="grid h-9 w-9 place-items-center rounded-full text-sugar-ink2 transition-colors hover:bg-sugar-blush disabled:opacity-30"
        >
          <ChevronLeft size={18} />
        </button>
        <span className="font-display text-lg font-bold text-sugar-ink">{monthLabel}</span>
        <button
          data-testid="cal-next"
          onClick={() => setView(new Date(year, month + 1, 1))}
          className="grid h-9 w-9 place-items-center rounded-full text-sugar-ink2 transition-colors hover:bg-sugar-blush"
        >
          <ChevronRight size={18} />
        </button>
      </div>
      <div className="mb-2 grid grid-cols-7 gap-1 text-center">
        {DOW.map((d) => <div key={d} className="text-[11px] font-bold uppercase text-sugar-muted">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          if (!d) return <div key={i} />;
          const key = iso(d);
          const isPast = d < minDate;
          const isBlocked = blockedSet.has(key);
          const disabled = isPast || isBlocked;
          const isSel = selected === key;
          return (
            <button
              key={key}
              data-testid={`cal-day-${key}`}
              disabled={disabled}
              onClick={() => onSelect(key)}
              className={[
                "relative aspect-square rounded-xl text-sm font-semibold transition-all",
                isSel ? "bg-sugar-pink text-white shadow-md scale-105" : "",
                !isSel && !disabled ? "bg-sugar-cream text-sugar-ink hover:bg-sugar-blush hover:scale-105" : "",
                disabled ? "cursor-not-allowed text-sugar-muted/50 line-through" : "",
              ].join(" ")}
            >
              {d.getDate()}
              {isBlocked && <span className="absolute inset-x-0 bottom-1 mx-auto block h-1 w-1 rounded-full bg-red-400" />}
            </button>
          );
        })}
      </div>
      <div className="mt-4 flex flex-wrap gap-4 text-[11px] text-sugar-ink2">
        <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-sugar-cream" /> Available</span>
        <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-sugar-pink" /> Selected</span>
        <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-white line-through border border-sugar-border" /> Blocked / too soon</span>
      </div>
    </div>
  );
};
